import Windows from "@assets/windowsIcon.png";
import MacOS from "@assets/macIcon.png";
import Ubuntu from "@assets/ubuntuIcon.png";
import Linux from "@assets/linuxIcon.png";
import Android from "@assets/androidIcon.png";
import Chrome from "@assets/chromeIcon.png";
import Safari from "@assets/safariIcon.png";
import FireFox from "@assets/firefoxIcon.png";
import Edge from "@assets/edgeIcon.png";
import Chromium from "@assets/chromiumIcon.png";

import { notiApi } from "@api";
import firebase from "../../firebaseConfig";

import { Browser, NotificationDevice, OS } from "src/types";
import { ToggleSwitch } from "../Common/ToggleSwitch/ToggleSwitch";
import { useEffect, useState } from "react";
import humanReadableSpan from "src/Utils/HumanReadableSpan";
import styles from "./NotificationSettings.module.css";
import { selectAuth, updateNotiId, useAppDispatch, useAppSelector } from "@redux";

export default function NotificationSettings() {
  const dispatch = useAppDispatch();
  const user = useAppSelector(selectAuth);
  const authToken = user?.tokenInfoAuth;

  let message;
  if (firebase.messaging.isSupported()) {
      message = firebase.messaging();
  }

  const [notificationDevicesLoading, setNotificationDevicesLoading] = useState<boolean>(false);
  const [notificationDevices, setNotificationDevices] = useState<NotificationDevice[] | null>(null);
  const [pushPermission, setPushPermission] = useState<NotificationPermission | "unsupported" | "unknown">("unknown");
  const [thisDevicePushToken, setThisDevicePushToken] = useState<string | null>(null);
  const [thisDevice, setThisDevice] = useState<NotificationDevice | null>(null);

  const getInitialPushPermission = (() => {
    if (!("Notification" in window)) return "unsupported";
    return Notification.permission;
  });

  const getInitialDevicePushToken: () => Promise<string | null> = (async () => {
    if (!firebase.messaging.isSupported()) return null;
    if (!message) return null;
    setNotificationDevicesLoading(true);
    await navigator.serviceWorker.ready;
    const token = await message.getToken({
      vapidKey: process.env.REACT_APP_FIREBASE_VAPIDKEY,
    });
    setNotificationDevicesLoading(false);
    return token || null;
  });

  useEffect(() => {
    const run = async () => {
      const initialPushPermission = getInitialPushPermission();
      setPushPermission(initialPushPermission);

      if (initialPushPermission === "granted") {
        setNotificationDevicesLoading(true);
        const initialDevicePushToken = await getInitialDevicePushToken();
        setNotificationDevicesLoading(false);
        if (initialDevicePushToken !== null) {
          setThisDevicePushToken(initialDevicePushToken);
          // await savePushTokenToServer(initialDevicePushToken); // this would automatically re-register on opening settings
          // await getNotificationDevices(); // refresh device list
        }
      }
    };

    run();
  }, []);
  
  useEffect(() => {
    if (thisDevicePushToken && notificationDevices) {
      const device = notificationDevices.find(device => device.pushToken === thisDevicePushToken) || null;
      setThisDevice(device);
    }
  }, [thisDevicePushToken, notificationDevices]);

  const savePushTokenToServer = async (tokenDevice: string) => {
    try {
      const os = detectOS();
      const browser = detectBrowser();
      await notiApi.storeDeviceToken({ token: tokenDevice,  os, browser });
    } catch (e) {
      console.error("Failed to save push token to server", e);
    }
  };

  const registerPushToken = async () => {
    let pushTokenToRegister: string | null = thisDevicePushToken;
    if (!pushTokenToRegister) {
      console.log("No device push token available to register, requesting new token");
      pushTokenToRegister = await message.getToken({
        vapidKey: process.env.REACT_APP_FIREBASE_VAPIDKEY,
      });
    }
    if (!pushTokenToRegister) {
      console.warn("No device push token available to register, even after requesting new token");
      return;
    }
    setNotificationDevicesLoading(true);
    await savePushTokenToServer(pushTokenToRegister);
    await getNotificationDevices(); // refresh device list
    setNotificationDevicesLoading(false);
  };

  const deletePushTokenFromServer = async (tokenId: number) => {
    setNotificationDevicesLoading(true);
    await notiApi.deleteDeviceToken({id: tokenId.toString()});
    await getNotificationDevices(); // refresh device list
    setNotificationDevicesLoading(false);
  };

  const requestPushPermission = async () => {
    console.log("Requesting push permission...");
    try {
      if (!message) {
        console.warn("Firebase messaging is not supported in this browser");
        alert("Firebase messaging is not supported in this browser");
        setPushPermission("unsupported");
        return;
      }
      if (!authToken) {
        console.warn("User not authenticated, requesting push permission aborted");
        alert("User not authenticated, requesting push permission aborted");
        return;
      }
      if (!("Notification" in window)) {
        console.warn("Notifications are not supported in this browser");
        alert("Notifications are not supported in this browser");
        setPushPermission("unsupported");
        return;
      }

      setNotificationDevicesLoading(true);
      const permission = await Notification.requestPermission();
      console.log("Push permission result:", permission);
      setPushPermission(permission);

      if (permission !== "granted") {
        console.warn("Push permission not granted. Permission:", permission);
        return;
      }

      await navigator.serviceWorker.ready;
      const reg = await navigator.serviceWorker.getRegistration();
      if (reg) {message.useServiceWorker(reg);}

      const tokenDevice = await message.getToken({vapidKey: process.env.REACT_APP_FIREBASE_VAPIDKEY});
      if (!tokenDevice) return;

      setThisDevicePushToken(tokenDevice);
      await savePushTokenToServer(tokenDevice);
      // dispatch(updateNotiId(res.data.id));
      await getNotificationDevices(); // refresh device list
      setNotificationDevicesLoading(false);
    } catch (e) {
      console.error("Failed to request push permission / register token", e);
    }
  };

  useEffect(() => {
    getNotificationDevices();
  }, []);

  const getNotificationDevices = async () => {
    try {
      setNotificationDevicesLoading(true);
      const res = await notiApi.listDeviceTokens();
      const deviceData = res.data.data.map(
        ({created_at, createdAt, updated_at, updatedAt, ...device}: any) => ({
          ...device,
          createdAt: createdAt ?? created_at,
          updatedAt: updatedAt ?? updated_at,
        })
      );
      setNotificationDevices(deviceData);
      setTimeout(() => {
        setNotificationDevicesLoading(false);        
      }, 500);
    } catch (err) {
      setNotificationDevicesLoading(false);
      return [];
    }
  };

  const togglePauseDevice = async (deviceId: number, paused: boolean) => {
    await notiApi.pauseDeviceToken({id: deviceId.toString(), pause: paused});
    await getNotificationDevices();
  };

  const deviceListHeader = (
    <div className={styles.deviceTable} style={{fontSize: "1rem", fontWeight: "bold"}}>
      <div>デバイス</div>
      <div>ステータス</div>
      <div>アクティブ</div>
      <div>更新</div>
      <div>消去</div>
    </div>
  );

  function generateDeviceRow(device: NotificationDevice | null, isThisDevice = false) {
    if (device === null && !isThisDevice) return null;
    const osString = device ? device.os : isThisDevice ? detectOS() : "Other";
    const browserString = device ? device.browser : isThisDevice ? detectBrowser() : "Other";
    const osImage = osString === "Windows" ? Windows : osString === "MacOS" ? MacOS : osString === "Ubuntu" ? Ubuntu : osString === "Linux" ? Linux : osString === "Android" ? Android : null;
    const browserImage = browserString === "Chrome" ? Chrome : browserString === "Safari" ? Safari : browserString === "FireFox" ? FireFox : browserString === "Edge" ? Edge : browserString === "Chromium" ? Chromium : null;
    const deviceStatus = device ? device.status : 0;
    const deviceErrors = device ? device.errors : 0;
    const tokenId = device ? device.id : 0;
    const devicePushToken = device ? device.pushToken : "";
    const devicePaused = device ? device.paused : false;
    const deviceUpdatedAt = device ? device.updatedAt : "";

    return (
      <div key={device?.id || "this-device"} className={styles.deviceTable} style={{backgroundColor: isThisDevice ? "#e0ffe0" : "transparent"}}>
        {isThisDevice && false && (<div className={styles.thisDeviceHeader}><span>このデバイス</span></div>)}
        <div className={styles.deviceCell}>{osImage ? <img src={osImage} style={{height: "2rem", width: "2rem"}} alt={osString} /> : <span>?</span>}{browserImage ? <img src={browserImage} style={{height: "2rem", width: "2rem"}} alt={browserString} /> : <span>?</span>}</div>
        <div className={styles.deviceCell}>{deviceStatus === 404 ? "❌" : deviceErrors === 0 ? "✅" : deviceErrors < 10 ? "⚠️" : "❌" }</div>
        <div className={styles.deviceCell} onClick={() => togglePauseDevice(tokenId, !devicePaused)}><ToggleSwitch checked={!devicePaused} scale={0.5} /></div>
        <div className={styles.deviceCell}>{humanReadableSpan(deviceUpdatedAt, 1)}前</div>
        <div className={styles.deviceCell} onClick={() => deletePushTokenFromServer(tokenId)}>🗑️</div>
      </div>
    );
  }

  const permissionNotGranted = (pushPermission !== "granted");
  const notRegistered = (thisDevice === null);

  const thisDeviceRequests = (!notRegistered) ? null : <div style={{textAlign: "start", gap: "1rem", display: "flex", alignItems: "center", marginBottom: "0.5rem", paddingLeft: "0.5rem"}}>
    {permissionNotGranted ? "このデバイスには通知を送信する許可が必要です" : "通知は無効になっています"}
    {permissionNotGranted ? <button className={styles.requestButton} onClick={requestPushPermission}>通知を許可する</button> : <button className={styles.requestButton} onClick={registerPushToken}>通知を有効にする</button>}
  </div>;
  const thisDeviceRow = (thisDevice !== null) ? <><span style={{fontWeight: "bold"}}>このデバイス</span>{deviceListHeader}{generateDeviceRow(thisDevice, true)}</> : null;

  const deviceList = notificationDevices
    ?.sort((a, b) => a.pushToken === thisDevicePushToken ? -1 : b.pushToken === thisDevicePushToken ? 1 : 0)
    .filter(device => device.pushToken !== thisDevicePushToken)
    .map((device) => { return generateDeviceRow(device, false); }) || [];

  return (
    <div style={{border: "1px solid #ddd", borderRadius: "0.5rem", padding: "0.5rem", marginBottom: "0.5rem"}}>
      {thisDeviceRequests}
      {thisDeviceRow}
      <span style={{ marginTop: "2rem", display: "block", fontWeight: "bold" }}>他のデバイス</span>
      {notificationDevicesLoading ? <div>Loading...</div> : <>
        {deviceListHeader}
        {deviceList}
      </>}
    </div>
  );
}

function detectOS(): OS {
  const uaData = (navigator as any).userAgentData;

  if (!uaData?.platform) {
    // Fallback path
    const ua = navigator.userAgent;
    const platform = navigator.platform || "";

    const isIOS =
      /iPhone|iPad|iPod/i.test(ua) ||
      (platform === "MacIntel" && (navigator as any).maxTouchPoints > 1);

    if (isIOS) return "iOS";
    if (/Android/i.test(ua)) return "Android";
    if (/Ubuntu/i.test(ua)) return "Ubuntu";
    if (/Linux/i.test(ua)) return "Linux";
    if (/Win/i.test(platform)) return "Windows";
    if (/Mac/i.test(platform)) return "MacOS";

    return "Other";
  }

  switch (uaData.platform) {
    case "Windows":
      return "Windows";
    case "macOS":
      return "MacOS";
    case "Android":
      return "Android";
    case "iOS":
      return "iOS";
    case "Linux":
      // Chromium does not differentiate distros
      return "Linux";
    default:
      return "Other";
  }
}

function detectBrowser(): Browser {
  const uaData = (navigator as any).userAgentData;

  if (!uaData?.brands) {
    // Fallback: extremely old browsers only
    const ua = navigator.userAgent;

    if (/Edg\//.test(ua)) return "Edge";
    if (/Firefox\//.test(ua)) return "FireFox";
    if (/CriOS\//.test(ua)) return "Chrome";
    if (/Chromium\//.test(ua)) return "Chromium";
    if (/Chrome\//.test(ua)) return "Chrome";
    if (/Safari\//.test(ua)) return "Safari";

    return "Other";
  }

  const brands = uaData.brands.map(b => b.brand);

  if (brands.includes("Microsoft Edge")) return "Edge";
  if (brands.includes("Google Chrome")) return "Chrome";
  if (brands.includes("Chromium")) return "Chromium";
  if (brands.includes("Firefox")) return "FireFox";
  if (brands.includes("Safari")) return "Safari";

  return "Other";
}
